import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Star,
  ArrowUpRight,
  Award,
  Zap,
  ShieldCheck,
  Layers,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import styles from './ProductFeatured.module.css';

const featuredProducts = [
  {
    id: 1,
    name: 'Food Wrap Paper',
    tagline: 'Grease-resistant wrap for QSR, bakery & takeaway brands.',
    status: 'Best Seller',
    icon: <Award size={14} />,
    img: '/images/products/food-wrap-papper (1).png',
    demand: 'High Demand',
    rating: '4.9',
    link: '/products/food-wrap-paper',
  },
  {
    id: 2,
    name: 'Paper Cup Sleeves',
    tagline: 'Premium heat insulation with high-quality print.',
    status: 'Popular',
    icon: <Zap size={14} />,
    img: '/images/products/Paper-Cup-Sleev.png',
    demand: 'Trending',
    rating: '4.8',
    link: '/products/paper-cup-sleeves',
  },
  {
    id: 3,
    name: 'PE Coated Rolls',
    tagline: 'Reliable barrier performance for industrial runs.',
    status: 'Industrial',
    icon: <ShieldCheck size={14} />,
    img: '/images/products/pe-coated-rolls.png',
    demand: 'B2B Favorite',
    rating: '5.0',
    link: '/products/pe-coated-rolls',
  },
  {
    id: 4,
    name: 'Pouches & Rolls',
    tagline: 'Flexible films for pouches, sachets & industrial converting.',
    status: 'Flexible Pack',
    icon: <Layers size={14} />,
    img: '/images/products/Pouches-Rolls1 (1).png',
    demand: 'High Demand',
    rating: '4.9',
    link: '/products/pouches-rolls',
  },
];

const highlights = [
  { label: 'Top picks', value: '4' },
  { label: 'Avg. rating', value: '4.9+' },
  { label: 'B2B ready', value: '100%' },
];

const easeOut = [0.22, 1, 0.36, 1];

const headerVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: easeOut },
  },
};

const statVariants = {
  hidden: { opacity: 0, scale: 0.85, y: 10 },
  visible: (i) => ({
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { delay: 0.15 + i * 0.08, duration: 0.45, ease: easeOut },
  }),
};

const panelVariants = {
  hidden: { opacity: 0, y: 32, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: easeOut, delay: 0.1 },
  },
};

const spotlightVariants = {
  hidden: { opacity: 0, x: -28 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: 'spring', stiffness: 80, damping: 16, delay: 0.2 },
  },
};

const sideCardVariants = {
  hidden: { opacity: 0, x: 24 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: {
      type: 'spring',
      stiffness: 90,
      damping: 18,
      delay: 0.28 + i * 0.1,
    },
  }),
};

const ProductFeatured = () => {
  const [spotlight, ...rest] = featuredProducts;

  return (
    <section className={styles.wrapper} aria-labelledby="featured-innovation-title">
      <div className={styles.bgAccent} aria-hidden="true">
        <span className={styles.orbOrange} />
        <span className={styles.orbGreen} />
      </div>

      <div className={styles.container}>
        <motion.header
          className={styles.header}
          variants={headerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
        >
          <div className={styles.headerTop}>
            <motion.span
              className={styles.kicker}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.05 }}
            >
              <motion.span
                className={styles.sparkleIcon}
                animate={{ rotate: [0, 12, -8, 0], scale: [1, 1.15, 1] }}
                transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 1.5 }}
              >
                <Sparkles size={14} />
              </motion.span>
              Industry Favorites
            </motion.span>
            <div className={styles.stats}>
              {highlights.map((item, i) => (
                <motion.div
                  key={item.label}
                  className={styles.stat}
                  custom={i}
                  variants={statVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  whileHover={{ y: -3, boxShadow: '0 8px 20px rgba(27,139,61,0.12)' }}
                >
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.h2
            id="featured-innovation-title"
            className={styles.mainTitle}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.12 }}
          >
            Featured <span className={styles.highlight}>Innovation</span>
          </motion.h2>
          <motion.p
            className={styles.headerSubtitle}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.22 }}
          >
            Our most requested product range — engineered for scale, branding, and
            consistent supply across India.
          </motion.p>
        </motion.header>

        <motion.div
          className={styles.panel}
          variants={panelVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          <motion.article
            className={`${styles.card} ${styles.spotlightCard}`}
            variants={spotlightVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            whileHover={{ y: -4, transition: { duration: 0.25 } }}
          >
            <motion.div
              className={styles.spotlightRibbon}
              initial={{ opacity: 0, x: -12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.35, type: 'spring', stiffness: 120 }}
            >
              <TrendingUp size={14} />
              #1 Most Ordered
            </motion.div>

            <Link
              to={spotlight.link}
              className={styles.spotlightMedia}
              aria-label={`View ${spotlight.name}`}
            >
              <motion.img
                src={spotlight.img}
                alt={spotlight.name}
                className={styles.spotlightImg}
                loading="eager"
                whileHover={{ scale: 1.06 }}
                transition={{ duration: 0.45, ease: easeOut }}
              />
              <span className={styles.statusBadge}>
                {spotlight.icon}
                {spotlight.status}
              </span>
            </Link>

            <div className={styles.spotlightBody}>
              <div className={styles.metaRow}>
                <span className={styles.demandLabel}>{spotlight.demand}</span>
                <span className={styles.ratingBox}>
                  <Star size={13} fill="#ff671b" color="#ff671b" />
                  {spotlight.rating}
                </span>
              </div>
              <h3 className={styles.spotlightTitle}>{spotlight.name}</h3>
              <p className={styles.spotlightText}>{spotlight.tagline}</p>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Link to={spotlight.link} className={styles.spotlightBtn}>
                  Explore Specs
                  <motion.span
                    animate={{ x: [0, 3, 0], y: [0, -3, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
                  >
                    <ArrowUpRight size={16} />
                  </motion.span>
                </Link>
              </motion.div>
            </div>
          </motion.article>

          <div className={styles.sideStack}>
            <p className={styles.sideStackLabel}>More top performers</p>
            {rest.map((item, index) => (
              <motion.article
                key={item.id}
                className={`${styles.card} ${styles.sideCard}`}
                custom={index}
                variants={sideCardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -3, transition: { duration: 0.22 } }}
              >
                <span className={styles.sideIndex} aria-hidden="true">
                  {String(index + 2).padStart(2, '0')}
                </span>

                <Link
                  to={item.link}
                  className={styles.sideThumb}
                  aria-label={`View ${item.name}`}
                >
                  <motion.img
                    src={item.img}
                    alt=""
                    className={styles.sideThumbImg}
                    loading="lazy"
                    whileHover={{ scale: 1.07 }}
                    transition={{ duration: 0.35 }}
                  />
                  <span className={styles.sideThumbGlow} aria-hidden="true" />
                </Link>

                <div className={styles.sideContent}>
                  <div className={styles.sideTopRow}>
                    <span className={styles.sideStatus}>
                      {item.icon}
                      {item.status}
                    </span>
                    <span className={styles.sideRating}>
                      <Star size={11} fill="#ff671b" color="#ff671b" />
                      {item.rating}
                    </span>
                  </div>

                  <h3 className={styles.sideTitle}>{item.name}</h3>
                  <p className={styles.sideTagline}>{item.tagline}</p>

                  <div className={styles.sideFooter}>
                    <span className={styles.demandLabel}>{item.demand}</span>
                    <Link to={item.link} className={styles.sideArrowBtn} aria-label={`View ${item.name}`}>
                      <ArrowUpRight size={16} />
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductFeatured;
